import { Stack, Typography } from "@mui/material";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { combineComparers, priorityComparer, stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import type { Category } from "../../../../../api/types/accessTypes";
import type { LookupObjectReference } from "../../../../../api/types/objectTypes";
import MultiselectAutocomplete from "../../../../common/MultiselectAutocomplete";
import InvestorCommunicationRecipientsList from "../../InvestorCommunicationRecipientsList";
import InvestorCommunicationContext from "../InvestorCommunicationContext";
import {
  isRecipientsSelectionChanged,
  setCategoryIdsAction,
  setFundIdsAction,
} from "../investorCommunicationEditState";
import InvestorCommunicationRecipientsPreviewBar from "./InvestorCommunicationRecipientsPreviewBar";

const getFundLabel = (fund: LookupObjectReference) => fund.primaryFieldValue;
const getFundValue = (fund: LookupObjectReference) => fund.objectId;
const getFundGroup = (fund: LookupObjectReference) =>
  fund.additionalFieldValues?.find((field) => field.fieldId === "Stage")?.fieldValue || "No stage";
const compareFundsByGroupAndName = combineComparers(
  priorityComparer((fund: LookupObjectReference) => getFundGroup(fund) === "Investment"),
  stringComparerBy(getFundGroup),
  stringComparerBy(getFundLabel)
);
const getCategoryLabel = (category: Category) => category.name;
const getCategoryValue = (category: Category) => category.externalId;

const InvestorCommunicationRecipientsForm = () => {
  const {
    state,
    setState,
    investorCommunication,
    recipients,
    readersByContactId,
    funds,
    categories,
    isSaving,
    validationErrors,
  } = useDefinedContext(InvestorCommunicationContext, "InvestorCommunicationContext");

  const fundOptions = [...funds].sort(compareFundsByGroupAndName);
  const selectedFunds = funds.filter((fund) => state.fundIds.includes(fund.objectId));
  const selectedCategories = categories.filter((category) => state.categoryIds.includes(category.externalId));
  const isSelectionChanged = isRecipientsSelectionChanged(state);

  return (
    <Stack spacing={3.5} sx={{ flex: 1, minHeight: 0 }}>
      <Stack spacing={0.5}>
        <Typography variant="subtitle1">Recipients</Typography>
        <Typography variant="caption" color="textSecondary">
          Choose who should receive this communication.
        </Typography>
      </Stack>

      <MultiselectAutocomplete<LookupObjectReference>
        label="Funds"
        required
        dense
        placeholder="Select funds"
        options={fundOptions}
        values={selectedFunds}
        disabled={isSaving}
        error={validationErrors.fundIds !== undefined}
        getOptionLabel={getFundLabel}
        getOptionValue={getFundValue}
        groupBy={getFundGroup}
        onSelected={(selected) => setState(setFundIdsAction(selected.map(getFundValue)))}
      />

      <MultiselectAutocomplete<Category>
        label="Categories"
        required
        dense
        placeholder="Select categories"
        options={categories}
        values={selectedCategories}
        disabled={isSaving}
        error={validationErrors.categoryIds !== undefined}
        getOptionLabel={getCategoryLabel}
        getOptionValue={getCategoryValue}
        onSelected={(selected) => setState(setCategoryIdsAction(selected.map(getCategoryValue)))}
      />

      <InvestorCommunicationRecipientsPreviewBar
        recipientsCount={recipients.length}
        isSelectionChanged={isSelectionChanged}
      />

      {!isSelectionChanged && (
        <InvestorCommunicationRecipientsList
          type={investorCommunication.type}
          recipients={recipients}
          readersByContactId={readersByContactId}
        />
      )}
    </Stack>
  );
};

export default InvestorCommunicationRecipientsForm;
